import {
  AnimatedPage,
  Banner,
  BannerSubtitle,
  BannerTitle,
  WideContainer,
  xlBumps,
  xxsBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { Navigate, Outlet, useLocation, useNavigate, useRoutes } from 'react-router'

import {
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { Footer } from '../../../../templates'
import { useProduct } from '../../../ProductProvider'
import { usePolicy } from '../../PolicyProvider'
import { TabHeader } from './components/TabHeader'
import { TabSelect } from './components/TabSelect'
import { useTabs } from './hooks'
import { TabItem } from './types'

const tKey = TKey('pages.policy-configuration')

const Content: FunctionComponent<{ tabs: TabItem[] }> = ({ tabs }) => {
  const { t } = useWriskTranslation()

  const navigate = useNavigate()
  const { pathname } = useLocation()

  const currentTab = useMemo(
    () =>
      tabs.find((tab) => {
        // Ensures that tab path must be a standalone part of the path to match
        const segments = pathname.split('/')
        return segments.includes(tab.path)
      }) ?? tabs[0],
    [tabs, pathname, location],
  )

  const onClick = useCallback(
    (tab: TabItem) => {
      // Navigate to '..' for the first tab to keep the URL clean
      const targetPath = tab === tabs[0] ? '..' : ['..', tab.path].join('/')
      navigate(targetPath)
    },
    [navigate, tabs],
  )

  return (
    <AnimatedPage pageId='policy-configuration-page' footer={<Footer />}>
      <Banner>
        <WideContainer flexDirection='column'>
          <BannerSubtitle mb={xxsBumps}>{t(tKey('subheader'))}</BannerSubtitle>
          <BannerTitle>{t(tKey('header'))}</BannerTitle>
        </WideContainer>
      </Banner>

      <WideContainer mb={xlBumps}>
        <TabHeader
          displayOn='desktop'
          tabs={tabs}
          current={currentTab}
          onClick={onClick}
        />
        <TabSelect
          displayOn='mobile'
          tabs={tabs}
          current={currentTab}
          onClick={onClick}
        />
      </WideContainer>

      <Outlet />
    </AnimatedPage>
  )
}

export const PolicyTabs: FunctionComponent = () => {
  const { policy } = usePolicy()
  const { product } = useProduct()

  const tabs = useTabs(policy, product)

  const routes = tabs
    .map(({ TabPage, path, id }, index) => ({
      id,
      index: index === 0,
      path: index === 0 ? undefined : path,
      element: <TabPage basePath='/' />,
    }))
    .concat({
      id: 'root',
      index: false,
      path: '*',
      element: <Navigate to='.' relative='path' replace={true} />,
    })

  return useRoutes([
    {
      element: <Content tabs={tabs} />,
      children: routes,
    },
  ])
}
